<template>
  <Primitive
    :as="as"
    :class="cn(buttonVariants({ variant, size }), $attrs.class ?? '')"
  >
    <span
      :class="[
        'inline-flex gap-2 items-center whitespace-nowrap transition duration-300 will-change-auto ',
        { '-translate-y-10': loading },
        { 'translate-y-0': !loading },
      ]"
    >
      <template v-if="$slots.startIcon">
        <span :class="cn(iconVariants({ size }))">
          <slot name="startIcon" />
        </span>
      </template>
      <slot />
      <template v-if="$slots.endIcon">
        <span :class="cn(iconVariants({ size }))">
          <slot name="endIcon" />
        </span>
      </template>
    </span>
    <span
      :class="[
        'absolute inset-0 z-10 flex items-center justify-center transition duration-300 will-change-auto',
        { 'translate-y-0': loading },
        { 'translate-y-full': !loading },
      ]"
    >
      <span class="animate-spin">
        <LoaderCircle class="size-4" />
      </span>
    </span>
  </Primitive>
</template>

<script setup>
import { Primitive } from 'radix-vue';
import { cva } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-vue-next';

import { cn } from '../../utils.js';

defineProps({
  variant: {
    type: String,
    default: 'primary',
  },
  size: {
    type: String,
    default: 'default',
  },
  as: {
    type: String,
    default: 'button',
  },
  loading: {
    type: Boolean,
    default: false,
  },
});
const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center rounded ring-offset-background transition-colors overflow-hidden',
    'text-sm font-semibold',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'before:rounded-inherit before:absolute before:inset-0 before:transition-opacity before:bg-black before:opacity-0',
    'hover:before:opacity-[0.08] active:before:opacity-[0.16]',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-accent-400 text-neutral-900 hover:text-neutral-900',
        tonal: 'bg-accent-200 text-neutral-900 hover:text-neutral-900',
        danger: 'bg-error-400 text-neutral-900',
        secondary:
          'bg-secondary-600 text-neutral-50 before:bg-neutral-300 active:before:opacity-[0.12]',
        outline:
          'border border-primary-400 bg-background text-neutral-300 before:bg-neutral-300 active:before:opacity-[0.12]',
        link: 'text-accent-400 hover:bg-transparent hover:before:opacity-0 active:before:opacity-0',
        ghost:
          'text-neutral-300 before:bg-neutral-300 active:before:opacity-[0.12]',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 px-2.5 text-xs',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

const iconVariants = cva(['inline-flex items-center justify-center'], {
  variants: {
    size: {
      default: 'size-6',
      sm: 'size-4',
      lg: 'size-8',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});
</script>
